import * as applicationActions from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.actions";
import * as applicationHooks from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.hooks";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import {
  EIntakeStep,
  IApplication,
} from "@rentiohq/shared-frontend/dist/types/application.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import api from "@rentiohq/shared-frontend/dist/utils/api/api.utils";
import {
  formatDate,
  formatTime,
} from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { Button, EmptyState, Icon } from "@rentiohq/web-shared/dist/components";
import { toast } from "@rentiohq/web-shared/dist/managers/Toast";
import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ConfirmInviteModal } from "./components/ConfirmInviteModal";
import { InviteSelectionModal } from "./components/InviteSelectionModal";
import { VisitCard } from "./components/VisitCard";
import { ClockIcon } from "./components/VisitIcons";
import * as S from "./VisitMode.styled";

/** date-fns patterns, so the day and month names follow the user's language. */
const DAY_KEY_PATTERN = "yyyy-MM-dd";
const DAY_TAB_PATTERN = "EEE d MMM";
const DAY_FULL_PATTERN = "EEEE d MMMM";

interface IVisit {
  id: string;
  applicationId: string;
  start: string;
  end: string;
  status?: string;
}

/** One time block with the people booked into it. */
interface IBlock {
  key: string;
  start: Date;
  end: Date;
  applications: IApplication[];
}

/**
 * Visit mode (DEV-3227): the screen for the person actually doing the viewings.
 *
 * Its own route rather than a mode inside the candidates screen, because the
 * mail sent an hour beforehand links straight here, and rendering it over the
 * loaded dashboard would mean fetching that whole page on a phone purely to
 * cover it up.
 *
 * Everything it does already existed; what did not exist was a way to do it at
 * the moment it is worth anything, standing in the property rather than back at
 * a desk an hour later.
 */
export const VisitMode = () => {
  const params = useParams<{ propertyId: string }>();
  const propertyId = params.propertyId ? +params.propertyId : undefined;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const { detail: property } = propertyHooks.useDetail({
    shouldRefetch: false,
    id: propertyId,
  });

  const { items: applications, refetch: refetchApplications } =
    applicationHooks.useGetAll({
      shouldRefetch: true,
      query: propertyId
        ? { filter: { propertyId: { eq: propertyId } } }
        : undefined,
    });

  const [visits, setVisits] = React.useState<IVisit[] | undefined>(undefined);
  const [openCardId, setOpenCardId] = React.useState<string | undefined>();
  // Who the invite dialog is about: everyone still to invite that day, or the
  // one person whose card asked for it. One dialog either way, because sending
  // a real mail should look the same however you got there.
  const [inviteFor, setInviteFor] = React.useState<
    IApplication[] | undefined
  >();
  const [hasLoadFailed, setHasLoadFailed] = React.useState(false);

  const loadVisits = React.useCallback(async () => {
    if (!propertyId) return;

    setHasLoadFailed(false);

    try {
      const result = await api.get<any>(
        `/cronofy/properties/${propertyId}/availability`,
        { isV2: true },
      );
      const data = result.data?.data || result.data || {};
      setVisits(
        (data.visits || []).filter((v: IVisit) => v.status !== "cancelled"),
      );
    } catch {
      // Not an empty day. A request that never answered says nothing about how
      // many people are coming, and telling a broker on a doorstep that nobody
      // is booked would be a definite, wrong answer to the one question this
      // screen exists for.
      setVisits(undefined);
      setHasLoadFailed(true);
    }
  }, [propertyId]);

  React.useEffect(() => {
    loadVisits();
  }, [loadVisits]);

  const applicationsById = React.useMemo(
    () =>
      new Map(
        (applications ?? [])
          // An archived candidacy is off every tab on the candidates screen, so
          // it has no business on a doorstep either. The appointment outlives
          // the archiving, and the visit leaves the day with the candidacy it
          // belongs to: a visit whose candidacy is not here is skipped.
          .filter(application => !application.archivedAt)
          .map(a => [a.id, a]),
      ),
    [applications],
  );

  /** Every day that has at least one visit, oldest first. */
  const days = React.useMemo(() => {
    const keys = new Set<string>();
    (visits ?? []).forEach(visit =>
      keys.add(formatDate(visit.start, DAY_KEY_PATTERN)),
    );
    return Array.from(keys).sort();
  }, [visits]);

  // The mail points at one day, so the link carries it. Without one we open on
  // today, or on the nearest upcoming day when there is nothing today.
  const requestedDay = searchParams.get("day") || undefined;
  const activeDay = React.useMemo(() => {
    if (requestedDay && days.includes(requestedDay)) return requestedDay;
    const today = formatDate(new Date(), DAY_KEY_PATTERN);
    if (days.includes(today)) return today;
    return days.find(day => day >= today) || days[days.length - 1];
  }, [requestedDay, days]);

  const isInvited = React.useCallback(
    (application: IApplication) =>
      application.step === EIntakeStep.candidateTenant ||
      application.step === EIntakeStep.tenant,
    [],
  );

  // What the day still owes, which is the invite: this screen exists to turn
  // visitors into candidate tenants, and a score or a note is a step on the way
  // rather than the end of it. A no-show is not counted, because somebody who
  // never turned up cannot be asked for their rental profile. That makes this
  // exactly the set the bulk button acts on.
  const needsInvite = React.useCallback(
    (application: IApplication) =>
      !application.noShowAt &&
      !isInvited(application) &&
      application.step !== EIntakeStep.notEligible,
    [isInvited],
  );

  /** Visits of one day, grouped by the block they were booked into. */
  const blocksForDay = React.useCallback(
    (day: string): IBlock[] => {
      const grouped = new Map<string, IBlock>();

      (visits ?? [])
        .filter(visit => formatDate(visit.start, DAY_KEY_PATTERN) === day)
        .forEach(visit => {
          const application = applicationsById.get(visit.applicationId);
          if (!application) return;

          const key = `${visit.start}-${visit.end}`;
          const block = grouped.get(key);
          if (block) {
            block.applications.push(application);
          } else {
            grouped.set(key, {
              key,
              start: new Date(visit.start),
              end: new Date(visit.end),
              applications: [application],
            });
          }
        });

      return Array.from(grouped.values()).sort(
        (a, b) => a.start.getTime() - b.start.getTime(),
      );
    },
    [visits, applicationsById],
  );

  const applicationsOnDay = React.useCallback(
    (day: string) => blocksForDay(day).flatMap(block => block.applications),
    [blocksForDay],
  );

  /** How many candidates that day still has to invite. */
  const dayAttentionCount = React.useCallback(
    (day: string) => applicationsOnDay(day).filter(needsInvite).length,
    [applicationsOnDay, needsInvite],
  );

  const activeDayApplications = activeDay ? applicationsOnDay(activeDay) : [];
  // Never crosses days: what you see is what you send, and this sends mail.
  const eligible = activeDayApplications.filter(
    application =>
      !application.noShowAt &&
      !isInvited(application) &&
      // Ruled out on the dashboard, so asking them for a rental profile would
      // undo a decision that was already made about them.
      application.step !== EIntakeStep.notEligible,
  );
  const skipped = activeDayApplications.filter(
    application => !!application.noShowAt,
  );

  // The same step change the candidate row performs, which is what already
  // sends the mail asking for a full rental profile. Nothing new is invented
  // here; only the moment it happens is.
  const inviteApplications = (applicationIds: string[]) => {
    applicationIds.forEach(id =>
      dispatch(
        applicationActions.updateApplicationStepStart.getAction({
          id,
          step: EIntakeStep.candidateTenant,
        }),
      ),
    );
    setInviteFor(undefined);
    toast({
      heading: getLocalizedText("visit_mode.invite.sent", {
        count: `${applicationIds.length}`,
      }),
      variation: "success",
    });
  };

  const handleClose = () => {
    navigate(`/properties/${propertyId}/applications`, {
      state: { step: EIntakeStep.visitor },
    });
  };

  const handleSelectDay = (day: string) => {
    searchParams.set("day", day);
    setSearchParams(searchParams, { replace: true });
  };

  const address = property ? formatAddress(property as any) : "";

  const renderBody = () => {
    if (hasLoadFailed) {
      return (
        <S.EmptyDay>
          {getLocalizedText("visit_mode.error.load")}
          <S.RetryAction>
            <Button appearance="outline" onClick={loadVisits}>
              {getLocalizedText("visit_booking.retry")}
            </Button>
          </S.RetryAction>
        </S.EmptyDay>
      );
    }

    if (visits === undefined) {
      return <EmptyState isLoading size="small" />;
    }

    if (activeDayApplications.length === 0) {
      return <S.EmptyDay>{getLocalizedText("visit_mode.empty")}</S.EmptyDay>;
    }

    return blocksForDay(activeDay as string).map(block => (
      <S.Block key={block.key}>
        <S.BlockHeader>
          <S.BlockTimeGroup>
            <S.IconBox>
              <ClockIcon />
            </S.IconBox>
            <S.BlockTime>
              {formatTime(block.start)} - {formatTime(block.end)}
            </S.BlockTime>
          </S.BlockTimeGroup>
          {/* Several people can share one block, and that is worth saying out
              loud: a half hour with three visitors is a different half hour
              from one with a single visitor. One visitor needs no count, their
              name is on the line below it. */}
          {block.applications.length > 1 && (
            <S.BlockCount>
              {getLocalizedText("visit_mode.block.people", {
                count: `${block.applications.length}`,
              })}
            </S.BlockCount>
          )}
        </S.BlockHeader>

        {block.applications.map(application => (
          <VisitCard
            key={application.id}
            application={application}
            isOpen={openCardId === application.id}
            onToggle={() =>
              setOpenCardId(
                openCardId === application.id ? undefined : application.id,
              )
            }
            isInvited={isInvited(application)}
            onInvite={() => setInviteFor([application])}
            onNoteChanged={refetchApplications}
          />
        ))}
      </S.Block>
    ));
  };

  return (
    <S.Frame>
      <S.Column>
        <S.Header>
          <S.HeaderTitles>
            <S.HeaderTitle>
              {getLocalizedText("visit_mode.title")}
            </S.HeaderTitle>
            <S.HeaderAddress title={address}>{address}</S.HeaderAddress>
            {days.length <= 1 && activeDay && (
              <S.HeaderDay>
                {formatDate(activeDay, DAY_FULL_PATTERN)}
              </S.HeaderDay>
            )}
          </S.HeaderTitles>
          {/* The same close the dialogs use, rather than a typographic cross. */}
          <Button appearance="plain" onClick={handleClose}>
            <Icon source="close" />
          </Button>
        </S.Header>

        {/* Inside the scroller, so the strip scrolls away with the list. A day
            is picked once and then worked through, and on a phone those rows
            were holding on to a band of screen that an opened card wanted. */}
        <S.Body>
          {days.length > 1 && (
            <S.DayTabs>
              {days.map(day => (
                <S.DayTab
                  key={day}
                  $isActive={day === activeDay}
                  onClick={() => handleSelectDay(day)}
                  type="button"
                >
                  {formatDate(day, DAY_TAB_PATTERN)}
                  {dayAttentionCount(day) > 0 && (
                    <S.DayTabCount $isActive={day === activeDay}>
                      {dayAttentionCount(day)}
                    </S.DayTabCount>
                  )}
                </S.DayTab>
              ))}
            </S.DayTabs>
          )}

          {renderBody()}
        </S.Body>

        {/* Away while a card is open: that is one candidate being written up,
            not the day being closed, and on a phone the bar was taking a band
            of screen from the card that needed it. It slides back on close. */}
        {eligible.length > 0 && (
          <S.BulkSlot $isVisible={!openCardId}>
            <S.BulkClip>
              <S.BulkBar $isVisible={!openCardId}>
                <Button
                  appearance="primary"
                  onClick={() => setInviteFor(eligible)}
                >
                  {getLocalizedText("visit_mode.bulk.action")}
                </Button>
                <S.BulkHint>
                  {getLocalizedText(
                    // Nobody needs telling that nobody failed to turn up.
                    skipped.length > 0
                      ? "visit_mode.bulk.hint.with_skipped"
                      : "visit_mode.bulk.hint",
                    {
                      // The short form the tabs above use: this line sits under a
                      // button, not in a sentence, and it is the same day the tab
                      // it belongs to already names.
                      day: activeDay
                        ? formatDate(activeDay, DAY_TAB_PATTERN)
                        : "",
                      ready: `${eligible.length}`,
                      skipped: `${skipped.length}`,
                    },
                  )}
                </S.BulkHint>
              </S.BulkBar>
            </S.BulkClip>
          </S.BulkSlot>
        )}
      </S.Column>

      {/* One candidate is a yes-or-no; several is a choice. A list with a single
          tick would be asking a question that answers itself. */}
      {inviteFor?.length === 1 && (
        <ConfirmInviteModal
          application={inviteFor[0]}
          onClose={() => setInviteFor(undefined)}
          onConfirm={() => inviteApplications([inviteFor[0].id])}
        />
      )}

      {inviteFor && inviteFor.length > 1 && (
        <InviteSelectionModal
          eligible={inviteFor}
          // Only worth listing when the dialog is about the whole day: on one
          // card, other people's no-shows are noise.
          skipped={inviteFor.length > 1 ? skipped : []}
          day={activeDay ? formatDate(activeDay, DAY_FULL_PATTERN) : ""}
          onClose={() => setInviteFor(undefined)}
          onConfirm={inviteApplications}
        />
      )}
    </S.Frame>
  );
};
